/* eslint-disable ember/use-ember-data-rfc-395-imports */
import type DS from 'ember-data';
import type { ModelSchema } from 'ember-data';
import { type ModelRegistry } from 'ember-data/model';
import ApplicationAdapter from 'garaje/adapters/application';
import config from 'garaje/config/environment';
import urlBuilder from 'garaje/utils/url-builder';

interface EventLogQuery {
  companyId?: number;
  [key: string]: unknown;
}

type QueryParams = Record<string, unknown>;

export default class EventLogAdapter extends ApplicationAdapter {
  host = config.apiOverwrittenHost;
  namespace = 'a/event-logs/api/v1';

  get headers(): Record<string, string> {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  }

  urlForQuery(query: EventLogQuery): string {
    const companyId = query.companyId;
    if (!companyId) {
      throw new Error('companyId is required for event-log queries');
    }
    delete query.companyId; // remove from query params since it's in the URL
    return urlBuilder.eventLogs.search(companyId);
  }

  /**
   * Override `query` so we can control how the query params
   * are serialized into the URL (and avoid `filter[]`)
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  query<K extends keyof ModelRegistry>(_store: DS.Store, _type: ModelSchema<K>, query: EventLogQuery) {
    const baseUrl = this.urlForQuery(query);

    const queryString = this.buildEventLogQueryString(query as QueryParams);
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    // no explicit return type here,
    // let TS infer whatever type `this.ajax` actually returns
    // so it matches ApplicationAdapter's `query` signature.
    return this.ajax(url, 'GET');
  }

  /**
   * Custom query string builder:
   * - `filter` => repeated keys (no [] suffix): ?filter=a&filter=b
   */
  buildEventLogQueryString(params: QueryParams): string {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    const sortedParams = this.sortQueryParams(params) as QueryParams;
    const parts: string[] = [];

    Object.keys(sortedParams).forEach((key) => {
      const value = sortedParams[key];

      if (value === null || value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        (value as unknown[]).forEach((v) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`);
      }
    });

    return parts.join('&');
  }
}
